import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import {
  ArrowRight, Play, FileText, BookOpen, History, Globe, FilePlus, FileSearch,
  MessageSquare, PenTool, Archive, RefreshCw, Shield, Lock, ClipboardList, Scale,
  CheckCircle, ChevronDown, Zap, ShoppingCart, Users, Building2, ChevronRight
} from 'lucide-react';
import {
  contractsHeroData, contractsStatsData, standardizationFeaturesData,
  marketplaceFlowData, traditionalFlowData, eSignatureIntegrationsData,
  contractLifecycleData, complianceFeaturesData, automationRulesData,
  contractsFAQData, contractsTestimonialData
} from '../../data/contractsData';

const iconMap = {
  FileText, BookOpen, History, Globe, FilePlus, FileSearch,
  MessageSquare, PenTool, Archive, RefreshCw, Shield, Lock, ClipboardList, Scale
};

// Hero Section
const ContractsHero = () => (
  <section className="relative min-h-screen overflow-hidden bg-dark-ellipse">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                         radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />
    </div>
    <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#8bcaf9]/30 rounded-full blur-3xl animate-pulse" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
        <div className="text-left">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
            <FileText className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">{contractsHeroData.tagline}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {contractsHeroData.title}
          </h1>
          <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl leading-relaxed">
            {contractsHeroData.subtitle}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contacto?source=demo">
              <Button size="lg" className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-8 py-6 rounded-full text-lg transition-all hover:scale-105 hover:shadow-xl group">
                {contractsHeroData.cta}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/contacto?source=demo">
              <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg transition-all hover:scale-105 group bg-transparent">
                <Play className="mr-2 w-5 h-5" />
                {contractsHeroData.secondaryCta}
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative hidden lg:block">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-1 shadow-2xl border border-white/20">
            <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-semibold">Contract Manager</span>
                </div>
                <span className="text-green-400 text-sm">En vivo</span>
              </div>
              
              {/* Contract Preview */}
              <div className="bg-white/5 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-white text-sm font-medium">Contrato de Servicios Cloud</span>
                  <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">Pendiente firma</span>
                </div>
                <div className="space-y-2 text-xs text-white/60">
                  <div className="flex justify-between"><span>Cliente:</span><span className="text-white">TechCorp Inc.</span></div>
                  <div className="flex justify-between"><span>Servicios:</span><span className="text-white">Microsoft 365 + Azure</span></div>
                  <div className="flex justify-between"><span>Valor:</span><span className="text-white">€2,450/mes</span></div>
                </div>
              </div>

              {/* Signature Status */}
              <div className="space-y-2 mb-4">
                {[
                  { name: 'Aprobación Legal', status: 'Completado', color: 'green' },
                  { name: 'Firma Cliente', status: 'Pendiente', color: 'yellow' },
                  { name: 'Activación', status: 'En espera', color: 'gray' }
                ].map((step, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${step.color === 'green' ? 'bg-green-400' : step.color === 'yellow' ? 'bg-yellow-400' : 'bg-gray-400'}`} />
                      <span className="text-white/80 text-sm">{step.name}</span>
                    </div>
                    <span className={`text-xs ${step.color === 'green' ? 'text-green-400' : step.color === 'yellow' ? 'text-yellow-400' : 'text-gray-400'}`}>
                      {step.status}
                    </span>
                  </div>
                ))}
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white font-bold">247</p>
                  <p className="text-[#8bcaf9] text-xs">Contratos/mes</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white font-bold">98%</p>
                  <p className="text-[#8bcaf9] text-xs">Tasa de firma</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white font-bold">&lt;24h</p>
                  <p className="text-[#8bcaf9] text-xs">Tiempo cierre</p>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl transform rotate-3 hover:rotate-0 transition-transform">
            <div className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-[#1297fd]" />
              <div>
                <p className="text-gray-900 font-bold text-sm">Firma Digital</p>
                <p className="text-green-500 text-xs font-medium">100% legal</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        {contractsStatsData.map((stat, idx) => (
          <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all hover:scale-105">
            <p className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</p>
            <p className="text-white/70 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="absolute bottom-0 left-0 right-0">
      <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
      </svg>
    </div>
  </section>
);

// Standardization Section
const StandardizationSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            Estandarización
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Contratos consistentes,{' '}
            <span className="text-[#1297fd]">sin errores</span>
          </h2>
          <p className="text-lg text-[#c3c3c5] mb-8 leading-relaxed">
            Estandariza todos tus contratos con templates inteligentes, cláusulas reutilizables y generación automática de documentos.
          </p>
          <div className="space-y-6">
            {standardizationFeaturesData.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={feature.id} className="flex gap-4 group">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#1297fd] transition-colors">{feature.title}</h4>
                    <p className="text-[#c3c3c5] text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-[#e3e4e6] shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-[#1297fd]" />
              <span className="font-semibold text-gray-900">Template de Contrato</span>
            </div>
            <div className="bg-white rounded-xl border border-[#e3e4e6] p-6 space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 bg-[#1297fd]/10 text-[#1297fd] rounded text-xs">Variable</span>
                <span className="text-gray-600">{"{{cliente.nombre}}"}</span>
              </div>
              <div className="h-2 bg-[#e3e4e6] rounded w-full" />
              <div className="h-2 bg-[#e3e4e6] rounded w-3/4" />
              <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Cláusula</span>
                <span className="text-gray-600">Términos de SLA</span>
              </div>
              <div className="h-2 bg-[#e3e4e6] rounded w-full" />
              <div className="h-2 bg-[#e3e4e6] rounded w-5/6" />
              <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">Condicional</span>
                <span className="text-gray-600">Si producto = Enterprise</span>
              </div>
              <div className="h-2 bg-[#e3e4e6] rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Flow Comparison Section
const FlowComparisonSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Flujos de Firma
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Integración en{' '}
          <span className="text-[#1297fd]">ambos mundos</span>
        </h2>
        <p className="text-lg text-[#c3c3c5] max-w-2xl mx-auto">
          Firma electrónica integrada tanto en el checkout de tu marketplace como en procesos tradicionales de venta enterprise.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Marketplace Flow */}
        <div className="bg-white rounded-3xl p-8 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-xl">{marketplaceFlowData.title}</h3>
              <p className="text-[#c3c3c5] text-sm">{marketplaceFlowData.description}</p>
            </div>
          </div>
          
          <div className="space-y-4 mb-6">
            {marketplaceFlowData.steps.map((step, idx) => (
              <div key={step.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-[#1297fd] text-white flex items-center justify-center text-sm font-bold">
                    {step.id}
                  </div>
                  {idx < marketplaceFlowData.steps.length - 1 && (
                    <div className="w-0.5 h-full bg-[#e3e4e6] my-1" />
                  )}
                </div>
                <div className="pb-4">
                  <h4 className="font-semibold text-gray-900">{step.title}</h4>
                  <p className="text-[#c3c3c5] text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#e3e4e6]/50 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Beneficios:</p>
            <div className="flex flex-wrap gap-2">
              {marketplaceFlowData.benefits.map((benefit) => (
                <span key={benefit} className="px-3 py-1 bg-white rounded-full text-xs text-[#1297fd] border border-[#8bcaf9]">
                  {benefit}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Traditional Flow */}
        <div className="bg-white rounded-3xl p-8 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-900">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-xl">{traditionalFlowData.title}</h3>
              <p className="text-[#c3c3c5] text-sm">{traditionalFlowData.description}</p>
            </div>
          </div>
          
          <div className="space-y-4 mb-6">
            {traditionalFlowData.steps.map((step, idx) => (
              <div key={step.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-bold">
                    {step.id}
                  </div>
                  {idx < traditionalFlowData.steps.length - 1 && (
                    <div className="w-0.5 h-full bg-[#e3e4e6] my-1" />
                  )}
                </div>
                <div className="pb-4">
                  <h4 className="font-semibold text-gray-900">{step.title}</h4>
                  <p className="text-[#c3c3c5] text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#e3e4e6]/50 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Beneficios:</p>
            <div className="flex flex-wrap gap-2">
              {traditionalFlowData.benefits.map((benefit) => (
                <span key={benefit} className="px-3 py-1 bg-white rounded-full text-xs text-gray-700 border border-gray-300">
                  {benefit}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// E-Signature Integrations Section
const ESignatureSection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4 border border-white/20">
          Integraciones de Firma
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          Conecta con los líderes en firma electrónica
        </h2>
        <p className="text-lg text-white/80 max-w-2xl mx-auto">
          Integración nativa con las principales plataformas de firma electrónica del mercado.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {eSignatureIntegrationsData.map((integration) => (
          <div key={integration.id} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-all group">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <PenTool className="w-7 h-7 text-[#1297fd]" />
            </div>
            <h3 className="font-bold text-white text-lg mb-2">{integration.name}</h3>
            <p className="text-white/70 text-sm mb-4">{integration.description}</p>
            <div className="flex flex-wrap gap-2">
              {integration.features.map((feature) => (
                <span key={feature} className="px-2 py-1 bg-white/10 rounded-lg text-xs text-white/90">
                  {feature}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Contract Lifecycle Section
const LifecycleSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Ciclo de Vida
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Gestión completa del{' '}
          <span className="text-[#1297fd]">contrato</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {contractLifecycleData.map((stage, idx) => {
          const IconComponent = iconMap[stage.icon];
          return (
            <div key={stage.id} className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl hover:-translate-y-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                  {IconComponent && <IconComponent className="w-5 h-5 text-white" />}
                </div>
                <div className="w-8 h-8 rounded-full bg-[#e3e4e6] flex items-center justify-center text-sm font-bold text-gray-500">
                  {idx + 1}
                </div>
              </div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#1297fd] transition-colors">{stage.title}</h3>
              <p className="text-[#c3c3c5] text-sm">{stage.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Compliance Section
const ComplianceSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div className="order-2 lg:order-1">
          <div className="grid grid-cols-2 gap-4">
            {complianceFeaturesData.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={feature.id} className="bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-lg group">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">{feature.title}</h4>
                  <p className="text-[#c3c3c5] text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            Cumplimiento
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Cumplimiento normativo{' '}
            <span className="text-[#1297fd]">garantizado</span>
          </h2>
          <p className="text-lg text-[#c3c3c5] mb-8 leading-relaxed">
            Todos los contratos cumplen con las normativas de firma electrónica más exigentes: eIDAS en Europa, ESIGN en USA y equivalentes en LATAM.
          </p>
          <ul className="space-y-3">
            {['Firma electrónica cualificada (QES)', 'Timestamping certificado', 'Almacenamiento seguro cifrado', 'Validez legal internacional'].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#1297fd]" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Automation Rules Section
const AutomationSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Automatización
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Reglas que trabajan{' '}
          <span className="text-[#1297fd]">por ti</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {automationRulesData.map((rule, idx) => (
          <div key={rule.id} className={`rounded-2xl p-6 ${idx === 0 ? 'bg-gradient-to-br from-[#1297fd] to-[#40aaf8] text-white' : 'bg-gradient-to-br from-gray-50 to-white border border-[#e3e4e6]'} hover:shadow-xl transition-all hover:-translate-y-1`}>
            <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium mb-4 ${idx === 0 ? 'bg-white/20 text-white' : 'bg-[#8bcaf9]/20 text-[#1297fd]'}`}>
              <Zap className="w-3 h-3" />
              {rule.trigger}
            </div>
            <h3 className={`font-bold text-lg mb-2 ${idx === 0 ? 'text-white' : 'text-gray-900'}`}>{rule.title}</h3>
            <p className={`text-sm ${idx === 0 ? 'text-white/80' : 'text-[#c3c3c5]'}`}>{rule.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Testimonial Section
const TestimonialSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="bg-white rounded-3xl p-12 border border-[#e3e4e6] shadow-xl">
        <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
          <span className="text-white text-2xl font-bold">"</span>
        </div>
        <blockquote className="text-xl sm:text-2xl text-gray-900 mb-8 leading-relaxed">
          {contractsTestimonialData.quote}
        </blockquote>
        <div>
          {contractsTestimonialData.author && <p className="font-bold text-gray-900">{contractsTestimonialData.author}</p>}
          <p className="text-[#1297fd]">{contractsTestimonialData.role}, {contractsTestimonialData.company}</p>
          {contractsTestimonialData.location && <p className="text-[#c3c3c5] text-sm">{contractsTestimonialData.location}</p>}
        </div>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">FAQ</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Preguntas frecuentes</h2>
        </div>
        <div className="space-y-4">
          {contractsFAQData.map((item, idx) => (
            <div key={idx} className={`rounded-2xl border-2 transition-all overflow-hidden ${openIndex === idx ? 'border-[#8bcaf9] bg-white shadow-lg' : 'border-[#e3e4e6] bg-white'}`}>
              <button onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)} className="w-full p-6 text-left flex items-center justify-between gap-4">
                <span className={`font-semibold text-lg ${openIndex === idx ? 'text-[#1297fd]' : 'text-gray-900'}`}>{item.question}</span>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${openIndex === idx ? 'rotate-180' : 'bg-[#e3e4e6]'}`} style={openIndex === idx ? { background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' } : {}}>
                  <ChevronDown className={`w-5 h-5 ${openIndex === idx ? 'text-white' : 'text-gray-600'}`} />
                </div>
              </button>
              <div className={`overflow-hidden transition-all ${openIndex === idx ? 'max-h-96' : 'max-h-0'}`}>
                <div className="px-6 pb-6"><p className="text-[#c3c3c5] leading-relaxed">{item.answer}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Automatiza tus contratos desde hoy</h2>
      <p className="text-lg text-white/80 mb-10">Reduce el tiempo de cierre, elimina errores y firma digitalmente con validez legal.</p>
      <div className="flex flex-wrap justify-center gap-4">
        <Link to="/contacto?source=demo">
          <Button size="lg" className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-10 py-6 rounded-full transition-all hover:scale-105 group">
            Comenzar ahora
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Button>
        </Link>
        <Link to="/contacto?source=demo">
          <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 rounded-full transition-all hover:scale-105 bg-transparent">
            Solicitar demo
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// Main Page Component
const ContractsPage = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Automatización de Contratos · Ciclo de vida contractual end-to-end"
        description="Gestiona plantillas, aprobaciones, firma electrónica y renovaciones de contratos desde una sola plataforma. Integraciones con DocuSign, Sign.com y más."
        keywords="CLM, contract lifecycle management, firma electrónica, DocuSign, contratos digitales, automatización legal"
        canonical="/automatizacion-contratos"
      />
      <Navbar />
      <ContractsHero />
      <StandardizationSection />
      <FlowComparisonSection />
      <ESignatureSection />
      <LifecycleSection />
      <ComplianceSection />
      <AutomationSection />
      <TestimonialSection />
      <FAQSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default ContractsPage;
